/**
 * @file auth.js
 * Přihlášení do administrace na straně klienta — jediné místo, které rozhoduje,
 * jestli je uživatel přihlášený a kam ho po přihlášení poslat.
 *
 * Proč: guard v routeru dřív kontroloval jen EXISTENCI tokenu v localStorage.
 * Token platí 24 h; kdo se vrátil druhý den, prošel guardem s prošlým tokenem,
 * server na každý dotaz odpověděl 403 (neplatný/prošlý JWT) a klient, který
 * hlídal jen 401, nechal uživatele na prázdném dashboardu. Na přihlašovací
 * stránku se dostal jen ručně a po přihlášení to vypadalo nahodile.
 */

export const TOKEN_KEY = 'token';
export const USER_KEY = 'user';
export const LOGIN_PATH = '/admin/login';
export const HOME_PATH = '/admin';
/** Hodnota ?duvod= — přihlašovací stránka podle ní řekne, proč tam uživatel je. */
export const DUVOD_VYPRSELO = 'vyprselo';

/**
 * Obsah JWT bez ověření podpisu — klient ho potřebuje jen kvůli `exp`.
 * Podpis ověřuje server; tady jde o to nepouštět uživatele s tokenem,
 * o kterém už víme, že neprojde.
 * @param {string} token
 * @returns {object|null}
 */
export function tokenPayload(token) {
  if (typeof token !== 'string') return null;
  const casti = token.split('.');
  if (casti.length !== 3) return null;
  try {
    const b64 = casti[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof atob === 'function'
      ? decodeURIComponent(escape(atob(b64)))
      : Buffer.from(b64, 'base64').toString('utf8');
    const p = JSON.parse(json);
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
}

/**
 * Je token použitelný? Strukturálně JWT a nevypršel (s 30s rezervou na
 * rozdíl hodin klienta a serveru).
 * @param {string|null} token
 * @param {number} [nowMs]
 */
export function isTokenValid(token, nowMs = Date.now()) {
  const p = tokenPayload(token);
  if (!p) return false;
  if (typeof p.exp === 'number' && p.exp * 1000 <= nowMs + 30 * 1000) return false;
  return true;
}

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function isLoggedIn(nowMs) {
  return isTokenValid(getToken(), nowMs);
}

export function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* soukromé okno apod. */ }
}

/**
 * Kam po přihlášení. Jen relativní cesta UVNITŘ administrace — cokoli jiného
 * (absolutní URL, //host, \\host, javascript:, jiná část aplikace, samotná
 * přihlašovací stránka) spadne na výchozí /admin. Brání open redirectu přes
 * odkaz typu /admin/login?redirect=https://podvodna.stranka.
 *
 * @param {unknown} raw hodnota z ?redirect=
 * @param {string} [fallback]
 * @returns {string}
 */
export function safeRedirect(raw, fallback = HOME_PATH) {
  const r = Array.isArray(raw) ? raw[0] : raw;
  if (typeof r !== 'string' || r.length === 0 || r.length > 512) return fallback;
  if (!r.startsWith('/')) return fallback;                 // jen relativní
  if (r.startsWith('//') || r.includes('\\')) return fallback;   // protocol-relative / Windows
  if (/[\u0000-\u001f\u007f]/.test(r)) return fallback;    // řídicí znaky (CR/LF, tab)
  let cesta;
  try {
    // Rozparsovat proti fiktivnímu originu: když výsledek není na témže
    // originu, byl to pokus o únik (např. /\t/evil nebo zakódované //).
    const u = new URL(r, 'https://roadpatrol.invalid');
    if (u.origin !== 'https://roadpatrol.invalid') return fallback;
    cesta = u.pathname;
    if (!(cesta === HOME_PATH || cesta.startsWith(HOME_PATH + '/'))) return fallback;
    if (cesta === LOGIN_PATH || cesta.startsWith(LOGIN_PATH + '/')) return fallback;
    return u.pathname + u.search + u.hash;
  } catch {
    return fallback;
  }
}

/**
 * Adresa přihlašovací stránky s návratem na `fromFullPath`.
 * @param {string} [fromFullPath]
 * @param {{ expired?: boolean }} [opts]
 */
export function loginLocation(fromFullPath, opts = {}) {
  const query = {};
  const back = safeRedirect(fromFullPath, '');
  if (back && back !== HOME_PATH) query.redirect = back;
  if (opts.expired) query.duvod = DUVOD_VYPRSELO;
  return { path: LOGIN_PATH, query };
}

/**
 * Router guard. Vrací `true` (pustit dál) nebo cílovou lokaci.
 *   - chráněná stránka bez platného tokenu → přihlášení s ?redirect=
 *     (a s ?duvod=vyprselo, když token byl, ale neplatí — uklidí se)
 *   - přihlášený uživatel na /admin/login → rovnou do administrace
 * @param {{ path: string, fullPath: string, query: object, meta: object }} to
 */
export function authGuard(to, nowMs) {
  const token = getToken();
  const platny = isTokenValid(token, nowMs);
  if (to.meta && to.meta.requiresAuth && !platny) {
    if (token) clearSession();
    return loginLocation(to.fullPath, { expired: !!token });
  }
  if (to.path === LOGIN_PATH && platny) {
    return safeRedirect(to.query && to.query.redirect);
  }
  return true;
}

/**
 * Axios interceptor: server odmítl token administrace (401 neznámý uživatel,
 * 403 neplatný/prošlý podpis) → uklidit a poslat na přihlášení s návratem.
 * Jen když požadavek token nesl a uživatel je v administraci — veřejné
 * stránky (formulář občana, mapa) tím nikdy nevyhodí.
 */
export function installAuthInterceptor(api, router) {
  api.interceptors.response.use(undefined, (error) => {
    const status = error?.response?.status;
    const slTokenem = !!error?.config?.headers?.Authorization;
    const route = router.currentRoute && router.currentRoute.value;
    const vAdministraci = route && route.path.startsWith(HOME_PATH) && route.path !== LOGIN_PATH;
    if ((status === 401 || status === 403) && slTokenem && vAdministraci) {
      clearSession();
      router.replace(loginLocation(route.fullPath, { expired: true }));
    }
    return Promise.reject(error);
  });
}
